import { gql, useMutation, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { ProductImagesV2 } from "components/shared/ProductImages";
import { useModals } from "ModalProvider";
import moment from "moment";
import PreviewQCReport from "pages/qc/components/PreviewQCReport";
import QcStatus from "pages/qc/components/QcStatus";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { Link } from "react-router-dom";
import { timeSince } from "utils/date";
import { DELETE_QC_REPORT } from "./graphql";

const FETCH_RECENT_QC_REPORTS = gql`
  query FETCH_RECENT_QC_REPORTS {
    qcReports: allQcReports(limit: 60) {
      id
      product {
        id
        number
        name
        images: computedImages(size: "300x300") {
          id
          url
        }
      }
      qty
      factory {
        id
        name
      }
      status
      po
      createdAt
      deletedAt
      sharedWithCustomerAt
    }
  }
`;

const RecentQCReports = () => {
  const { loading, error, data } = useQuery(FETCH_RECENT_QC_REPORTS);
  const modal = useModals();

  if (loading) return <InlineSpinner />;
  if (error) return <Errors error={error} />;

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl flex flex-col h-full overflow-hidden">
      <h4 className="flex-shrink-0">Recent Final Reports</h4>
      <div className="mt-2 overflow-y-auto flex-1 px-2">
        {data.qcReports
          .filter((i) => !i.deletedAt && !i.po)
          .map((report) => (
            <div key={report.id} className="border-b dark:border-gray-700 py-2">
              <div className="float-right pl-2 pt-2">
                <QcStatus status={report.status} />
              </div>
              <div className="flex flex-wrap mt-2">
                <ProductImagesV2 images={report.product.images} size="w-8" />
              </div>

              <div className="mt-1">
                {report.product.name} &times; {report.qty}
              </div>

              {report.sharedWithCustomerAt ? (
                <div className="mt-2 text-purple-500 flex items-center space-x-2">
                  <label>Shared with Customer</label>
                  <IoMdCheckmarkCircle size={21} />
                </div>
              ) : null}

              <div className="mt-1">
                <div className="opacity-60">
                  {timeSince(moment.utc(report.createdAt))} Ago
                </div>
                <div className="mt-1 flex justify-end items-center gap-4 text-sm">
                  <button
                    className="text-blue-600 dark:text-blue-400"
                    onClick={() =>
                      modal.present({
                        fullscreen: true,
                        children: <PreviewQCReport id={report.id} />,
                      })
                    }
                    type="button"
                  >
                    预览
                  </button>
                  <Link to={`/qc/reports/${report.id}`}>编辑</Link>
                  <DeleteButton id={report.id} />
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

const DeleteButton = ({ id }) => {
  const [deleteReport, deleteReportRes] = useMutation(DELETE_QC_REPORT, {
    variables: { id },
    onCompleted() {
      Alert("success", "Report deleted.");
    },
    onError(error) {
      Alert("error", error.message);
    },
  });

  return (
    <Button
      loading={deleteReportRes.loading}
      onClick={() => {
        if (window.confirm(`Are you sure to delete this qc report?`))
          deleteReport();
      }}
      danger
    >
      删除
    </Button>
  );
};

export default RecentQCReports;
